import { useEffect, useState } from 'react';

import { useInterval } from '.';

interface UseProgressCycleInput {
    /**
     * Size of a single interval used to increment the returned `progress`. A bigger `delay` means
     * more slowly increasing progress.
     * @defaultValue 250
     */
    delay?: number;
    /**
     * Callback invoked when progress reaches 100
     */
    onComplete?(): void;
    /**
     * The amount to increase the returned `progress` by on each completed interval
     * @defaultValue 10
     */
    stepSize?: number;
}

interface UseProgressCycleResult {
    /**
     * Allows you to set the `canceled` state of the progress bar. When `canceled === true`, the
     * progress bar does not move.
     * @param canceled - next state of `canceled`
     */
    setCanceled(canceled: boolean): void;
    /**
     * Whether the progress bar is canceled (stopped)
     */
    canceled: boolean;
    /**
     * Current progress amount out of 100 total
     */
    progress: number;
    /**
     * Method that will reset `progress` to 0
     */
    resetProgress(): void;
}

/**
 * Exposes some data and methods for faking a progress bar cycle. The progress starts incrementing
 * immediately by default.
 * @param stepSize - the amount to increase the progress by on each completed interval
 * @param delay - size of a single interval
 * @returns a {@link UseProgressCycleResult} object
 */
export const useProgressCycle = (
    input?: UseProgressCycleInput,
): UseProgressCycleResult => {
    const stepSize = input?.stepSize ?? 10;
    const delay = input?.delay ?? 250;
    const [progress, setProgress] = useState(0);
    const [awaitingReset, setAwaitingReset] = useState(false);
    const [canceled, setCanceled] = useState(false);

    useInterval(() => {
        if (!awaitingReset && !canceled && progress < 100) {
            const nextProgress = progress + stepSize;

            setProgress(nextProgress);

            if (nextProgress >= 100 && input?.onComplete) {
                input.onComplete();
            }
        }
    }, delay);

    useEffect(() => {
        if (awaitingReset) {
            setProgress(0);

            const id = setTimeout((): void => {
                setAwaitingReset(false);
            }, 1000);

            return (): void => {
                clearTimeout(id);
            };
        }

        return undefined;
    }, [awaitingReset]);

    return {
        canceled,
        setCanceled,
        progress,
        resetProgress: (): void => {
            setAwaitingReset(true);
        },
    };
};
